/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { getCustomStyles } from "Config/Config";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper";

const SaveTemperaturePopup = ({
  afterActionClose = () => {},
  selectedTemperatureId = null, // Default value for selectedTemperatureId
  setSelectedTemperatureId = () => {},
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));

  const userObj = {
    label: `${currentUser.name ?? currentUser.email} ${
      currentUser.surname ?? ""
    }`,
    value: currentUser._id,
  };

  // States for input fields
  const [celcius, setCelcius] = useState("");
  const [fahrenheit, setFahrenheit] = useState("");
  const [temperatureDate, setTemperatureDate] = useState(""); // New state for date
  const [assignTo, setAssignTo] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] =
    useState(false); /* New state for loading temperature details */
  const [errors, setErrors] = useState({});
  const offcanvasBodyRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!celcius) {
      newErrors.celcius = t("Please enter Celsius value!");
    } else if (isNaN(celcius) || celcius <= 0) {
      newErrors.celcius = t("Please enter a valid Celsius number!");
    }
    if (!fahrenheit) {
      newErrors.fahrenheit = t("Please enter Fahrenheit value!");
    } else if (isNaN(fahrenheit) || fahrenheit <= 0) {
      newErrors.fahrenheit = t("Please enter a valid Fahrenheit number!");
    }
    if (!temperatureDate) {
      newErrors.temperatureDate = t("Please select a date!");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Async load options for Assign To (from AddAllergyModal)
  const loadAssineeOptions = async (inputValue) => {
    try {
      const options = await getMemberOptions(inputValue);
      return options;
    } catch (error) {
      return [];
    }
  };

  //function for get temperature details
  const getTemperatureDetails = async () => {
    try {
      setIsLoading(true); /* Set loading to true before API call */
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_TEMPERATURE_BY_ID +
        `/${selectedTemperatureId}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const temperatureData = response.data;

        setCelcius(temperatureData.celcius);
        setFahrenheit(temperatureData.fahrenheit);
        setTemperatureDate(temperatureData.date);
        setAssignTo(temperatureData?.uservalue);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(
        false
      ); /* Set loading to false after API call (success or failure) */
    }
  };

  const saveTemperatureHandler = async () => {
    if (!validateForm()) {
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    try {
      setIsSaving(true);
      const apiData = {
        celcius: parseFloat(celcius),
        fahrenheit: parseFloat(fahrenheit),
        date: temperatureDate,
        user: assignTo ? assignTo?.value : null,
      };

      let requestUrl = url.API_BASE_URL;
      let response = {};

      if (selectedTemperatureId) {
        requestUrl +=
          url.API_UPDATE_TEMPERATURE +
          `/${selectedTemperatureId}`;
        response = await putData(requestUrl, apiData);
      } else {
        requestUrl += url.API_CREATE_TEMPERATURE;
        response = await postData(requestUrl, apiData);
      }

      setIsSaving(false);

      if (response.status) {
        resetHandler();
        window.bootstrap.Offcanvas.getInstance(
          document.querySelector("#saveTemperatureOffCanvas")
        ).hide();
        afterActionClose([response.data]);
      }
    } catch (error) {
      console.error("Error saving temperature:", error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetHandler = () => {
    setCelcius("");
    setFahrenheit("");
    setTemperatureDate("");
    setAssignTo(null);
    setErrors({});
    setSelectedTemperatureId(null);
  };

  useEffect(() => {
    if (selectedTemperatureId) {
      getTemperatureDetails();
    }
  }, [selectedTemperatureId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveTemperatureOffCanvas"
      aria-labelledby="saveTemperatureOffCanvas"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              eco
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="saveTemperatureOffCanvas"
            >
              {selectedTemperatureId
                ? t("Edit Temperature")
                : t("Save Temperature")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {selectedTemperatureId
                ? t("Manage existing temperature details")
                : t("Enter and save temperature readings")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4" ref={offcanvasBodyRef}>
        {isLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 z-index-1050">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">
                {t("Loading temperature details...")}
              </span>
            </div>
            <p className="text-primary mt-3 fw-bold">
              {t("Loading temperature details...")}
            </p>
          </div>
        ) : null}

        {!isLoading && (
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
            <form
              onSubmit={(e) => e.preventDefault()}
              className="d-flex flex-column h-100"
            >
              <div className="fields-container flex-fill">
                {/* Celsius and Fahrenheit on the same line */}
                <div className="row">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="celcius"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Celsius (°C) *
                      </label>
                      <input
                        type="number"
                        id="celcius"
                        className={`form-control fs-sm shadow-none ${
                          errors.celcius
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="e.g., 37.0"
                        value={celcius}
                        onChange={(e) => {
                          setCelcius(e.target.value);
                          if (errors.celcius) {
                            setErrors((prev) => ({ ...prev, celcius: "" }));
                          }
                        }}
                      />
                      {errors.celcius && (
                        <div className="invalid-feedback">{errors.celcius}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="fahrenheit"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Fahrenheit (°F) *
                      </label>
                      <input
                        type="number"
                        id="fahrenheit"
                        className={`form-control fs-sm shadow-none ${
                          errors.fahrenheit
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="e.g., 98.6"
                        value={fahrenheit}
                        onChange={(e) => {
                          setFahrenheit(e.target.value);
                          if (errors.fahrenheit) {
                            setErrors((prev) => ({ ...prev, fahrenheit: "" }));
                          }
                        }}
                      />
                      {errors.fahrenheit && (
                        <div className="invalid-feedback">
                          {errors.fahrenheit}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Date field */}
                <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label
                    htmlFor="temperatureDate"
                    className="d-block fs-sm fw-semibold mb-2 text-dark"
                  >
                    Date *
                  </label>
                  <input
                    type="date"
                    id="temperatureDate"
                    className={`form-control fs-sm shadow-none ${
                      errors.temperatureDate
                        ? "is-invalid"
                        : "border border-gray-300"
                    }`}
                    value={temperatureDate}
                    onChange={(e) => {
                      setTemperatureDate(e.target.value);
                      if (errors.temperatureDate) {
                        setErrors((prev) => ({
                          ...prev,
                          temperatureDate: "",
                        }));
                      }
                    }}
                  />
                  {errors.temperatureDate && (
                    <div className="invalid-feedback">
                      {errors.temperatureDate}
                    </div>
                  )}
                </div>

                {/* Assign To field */}
                <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Assign To
                  </label>
                  <AsyncSelect
                    isClearable
                    name="assignTo"
                    loadOptions={loadAssineeOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    placeholder={t("Select Assignee")}
                    styles={getCustomStyles()}
                    value={assignTo}
                    onChange={setAssignTo}
                  />
                </div>
              </div>

              {/* Temperature Interpretation Info */}
              <div className="p-4 border rounded-10 shadow-sm bg-gradient-light position-relative overflow-hidden">
                <div className="row align-items-center mb-3">
                  <div className="col">
                    <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <span className="material-symbols-outlined text-primary">
                        assessment
                      </span>
                      Temperature Interpretation
                    </h5>
                    <p className="text-muted fs-sm mb-0">
                      Reference ranges for adults
                    </p>
                  </div>
                </div>

                <div className="interpretation-grid">
                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Normal
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Celsius:</strong> 36.1°C - 37.2°C
                      </span>
                      <span>
                        <strong>Fahrenheit:</strong> 97°F - 99°F
                      </span>
                    </div>
                  </div>

                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Fever
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Celsius:</strong> ≥37.8°C
                      </span>
                      <span>
                        <strong>Fahrenheit:</strong> ≥100.4°F
                      </span>
                    </div>
                  </div>

                  <div className="interpretation-item p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Hypothermia
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Celsius:</strong> {"<"}35°C
                      </span>
                      <span>
                        <strong>Fahrenheit:</strong> {"<"}95°F
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-top border-gray-200">
                  <p className="text-muted fs-xs mb-0 d-flex align-items-center gap-1">
                    <span className="material-symbols-outlined fs-6">info</span>
                    Consult your healthcare provider for proper interpretation
                    and treatment recommendations.
                  </p>
                </div>
              </div>

              <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={saveTemperatureHandler}
                  style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                  disabled={isSaving ? true : false}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined me-1">
                        save
                      </span>{" "}
                      {t("Save Temperature")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveTemperaturePopup;
